import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppParams, useAppSelector } from "@app/hooks";
import { selectCurrentBuilder } from "@app/features/builder/builderSlice";
import { useCreateActivityMutation, useContextAwareGetLandUseChange, useContextAwareGetModulesByActivityId } from "@hooks/index";
import { selectCurrentProject } from "@app/features/project/projectSlice";
import { clearProgressiveModules } from "@app/features/setupAssistant/setupAssistantSlice";
import { useFirebaseHook, EventTypes } from "@utils/firebaseUtils";
import useErrorMessage from "@utils/useErrorMessage";
import type { FormikValues } from "formik";
import type { ActivityCreatePayload } from "@app/features/activities/activityTypes";
import type { ContextModules, SetupAssistantForm } from "../setupAssistant.types";
import type { FinalForm } from "../SetupAssistantFinish";
import {
	BEModulesClassNames,
	BEModulesObj,
	FEModules,
	FEModulesObj,
	isInFEModules,
} from "@utils/moduleList";
import { landUseOptions } from "../setupAssistantUtils";
import { toActivityPayloadFields, type ActivityFormActivity } from "@pages/activity/activityForm";
import { ModuleTypeRegistry } from "@utils/moduleTypeRegistry";
import { useModuleTypeRegistry } from "@utils/useModuleTypeRegistry";

const getLUCBeModules = ({
	landUse_start,
	landUse_w,
	landUse_wo,
}: {
	landUse_start?: number;
	landUse_w?: number;
	landUse_wo?: number;
}) => {
	const startModules =
		landUseOptions.find((landOption) => landOption.id === landUse_start)
			?.derivedModules ?? [];
	const withModules =
		landUseOptions.find((landOption) => landOption.id === landUse_w)
			?.derivedModules ?? [];
	const withoutModules =
		landUseOptions.find((landOption) => landOption.id === landUse_wo)
			?.derivedModules ?? [];
	return {
		startModules,
		withModules,
		withoutModules,
	};
};

export const useSetupAssistantFinish = (
	modules: ContextModules[],
	values: SetupAssistantForm,
	onSuccess?: () => void 
) => {
	const { activityId } = useAppParams();
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { project } = useAppSelector(selectCurrentProject);
	const { fireEvent } = useFirebaseHook();

	// Activity data fetching
	const lucId = moduleList.find((module) => module.id === FEModules.LandUseChange)?.uniqueId;
	const id = useMemo(() => (activityId ? +activityId : null), [activityId]);
	const isEdit = useMemo<boolean>(() => !!id, [id]);
	
	const { data: modulesData, isLoading: modulesIsLoading } = useContextAwareGetModulesByActivityId(id ?? 0, { skip: !id });
	const { data: lucData, isLoading: lucIsLoading } = useContextAwareGetLandUseChange(lucId ?? 0, {
		refetchOnMountOrArgChange: true,
		skip: !isEdit || !lucId
	});

	// Form submission
	const { isLoading: isRegistryLoading, registry: moduleRegistry } = useModuleTypeRegistry();
	const [buildActivity, { isLoading, isError, isSuccess: isBuildSuccess, error, reset }] = useCreateActivityMutation();
	const { errorMsg } = useErrorMessage({ error, isError });

	// Confirmation modal state
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [hasConfirmed, setHasConfirmed] = useState(false);
	const pendingSubmitRef = useRef<(() => Promise<void>) | null>(null);

	// Computed values
	const preSelected = useMemo(
		() => isEdit 
			? modulesData?.map((mod) => {
				const className = moduleRegistry.getClassNameById(mod.module_type);
				if (!className) {
					console.warn(`Module type ID ${mod.module_type} not found in registry`);
					return undefined;
				}
				return `${BEModulesObj[className as keyof typeof BEModulesObj]}`;
			}).filter((item): item is string => item !== undefined) ?? [] 
			: (modules.map((mod) => `${mod.module}`) ?? []),
		[modules, isEdit, modulesData, moduleRegistry],
	);

	const area = useMemo(() => {
		if (!isEdit) return values.hectares;
		if (lucData?.module.area) return lucData?.module.area;
		return modulesData?.find(module => module?.area)?.area;
	}, [isEdit, lucData, modulesData, values.hectares]);


	const lucs = useMemo(() => isEdit ? {
		landUse_start: lucData?.module.module_type_start && moduleRegistry.getClassNameById(lucData?.module.module_type_start) ? BEModulesObj[moduleRegistry.getClassNameById(lucData?.module.module_type_start) as keyof typeof BEModulesObj] : undefined,
		landUse_w: lucData?.module.module_type_w && moduleRegistry.getClassNameById(lucData?.module.module_type_w) ? BEModulesObj[moduleRegistry.getClassNameById(lucData?.module.module_type_w) as keyof typeof BEModulesObj] : undefined,
		landUse_wo: lucData?.module.module_type_wo && moduleRegistry.getClassNameById(lucData?.module.module_type_wo) ? BEModulesObj[moduleRegistry.getClassNameById(lucData?.module.module_type_wo) as keyof typeof BEModulesObj] : undefined,
	} : {
		landUse_start: values.previousLandUse,
		landUse_w: values.mainActivityFocus,
		landUse_wo: values.withoutProjectLandUse ?? values.previousLandUse,
	}, [isEdit, values, lucData, moduleRegistry]);

	// Event handlers
	const handleCancel = useCallback(() => {
		setShowConfirmModal(false);
		setHasConfirmed(false);
		pendingSubmitRef.current = null;
	}, []);

	const handleConfirm = useCallback((submitForm: () => Promise<void>) => {
		setShowConfirmModal(false);
		setHasConfirmed(true);
		pendingSubmitRef.current = submitForm;
	}, []);

	useEffect(() => {
		if (hasConfirmed && pendingSubmitRef.current) {
			const submitForm = pendingSubmitRef.current;
			pendingSubmitRef.current = null;
			submitForm();
		}
	}, [hasConfirmed]);

	useEffect(() => {
		if (isBuildSuccess) {
			dispatch(clearProgressiveModules());
			if (onSuccess) {
				onSuccess(); // Call the success callback instead of navigating
				// Reset the mutation state to prevent the effect from running again
				reset();
			} else {
				navigate(isEdit ? "../edit" : "../activities", { relative: "path" });
			}
			setHasConfirmed(false);
		}
	}, [isBuildSuccess, navigate, dispatch, isEdit, onSuccess, reset]);

	const createActivityPayload = useCallback((formValues: FinalForm): ActivityCreatePayload => {
		const localValues = { ...formValues };
		
		// Clear land use values if LandUseChange module is not selected
		if (!localValues.modulesList.includes(`${FEModules.LandUseChange}`)) {
			localValues.landUse_start = undefined;
			localValues.landUse_w = undefined;
			localValues.landUse_wo = undefined;
		}

		// Build module list
		const beModuleList = localValues.modulesList.reduce<BEModulesClassNames[]>((accum, current) => {
				const beModule = isInFEModules(Number(current))
					? FEModulesObj[Number(current) as FEModules]?.beModuleClassName
					: undefined;
				return beModule && beModule !== BEModulesClassNames.LandUseChange
					? [...accum, beModule]
					: accum;
			},
			[],
		).map(module => moduleRegistry.getIdByClassName(module) ?? 0);

		// Build land use change object
		const { startModules, withModules, withoutModules } = getLUCBeModules({
			landUse_start: localValues.landUse_start,
			landUse_w: localValues.landUse_w,
			landUse_wo: localValues.landUse_wo,
		});

		const landUseChange = !localValues.modulesList.includes(`${FEModules.LandUseChange}`)
			? null
			: {
				module_type_start: startModules.length && FEModulesObj[startModules[0] as FEModules].beModuleClassName
					? moduleRegistry.getIdByClassName(FEModulesObj[startModules[0] as FEModules].beModuleClassName)
					: undefined,
				module_type_w: withModules.length && FEModulesObj[withModules[0] as FEModules].beModuleClassName
					? moduleRegistry.getIdByClassName(FEModulesObj[withModules[0] as FEModules].beModuleClassName)
					: undefined,
				module_type_wo: withoutModules.length && FEModulesObj[withoutModules[0] as FEModules].beModuleClassName
					? moduleRegistry.getIdByClassName(FEModulesObj[withoutModules[0] as FEModules].beModuleClassName)
					: undefined,
			};

		// Build base payload
		let payload: ActivityCreatePayload = {
			module_types: beModuleList.filter(
				(mod) =>
					![
						landUseChange?.module_type_start,
						landUseChange?.module_type_w,
						landUseChange?.module_type_wo,
					].includes(mod),
			),
			land_use_change: landUseChange,
			project: project?.id ?? 0,
			...toActivityPayloadFields(values.activity ?? ({} as ActivityFormActivity), project),
		};

		// Add optional fields
		if (localValues.area) payload = { ...payload, area: localValues.area };
		if (isEdit) payload = { ...payload, activity_id: id };

		return payload;
	}, [values, project, id, isEdit]);

	const handleSubmit = useCallback(async (formValues: FormikValues) => {
		if (!hasConfirmed && isEdit) {
			setShowConfirmModal(true);
			return;
		}

		const payload = createActivityPayload(formValues as FinalForm);

		try {
			await buildActivity(payload).unwrap();
			fireEvent(EventTypes.create_activity);
		} catch (err) {
			console.error(err);
			setHasConfirmed(false);
		}
	}, [hasConfirmed, isEdit, createActivityPayload, buildActivity, fireEvent]);

	return {
		// State
		id,
		isEdit,
		isLoading: lucIsLoading || modulesIsLoading,
		isSubmitting: isLoading || isRegistryLoading,
		isError,
		errorMsg,
		showConfirmModal,
		hasConfirmed,
		
		// Data
		preSelected,
		area,
		lucs,
		
		// Handlers
		handleSubmit,
		handleCancel,
		handleConfirm,
	};
}; 